/**
 * CDM-PDD Version 12.0 導航配置
 * 完整對應 UNFCCC CDM-PDD-FORM Version 12.0 (2021-10-08)
 */
import {
  InfoCircleOutlined,
  FileTextOutlined,
  ExperimentOutlined,
  ClockCircleOutlined,
  GlobalOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  PaperClipOutlined,
} from '@ant-design/icons';

export interface PDDNavItem {
  key: string;
  label: string;
  labelEn: string;
  icon?: any;
  badge?: 'NEW' | 'MOVED' | 'UPGRADED';
  required?: boolean; // 標註 v12.0 必填項目
  children?: PDDNavItem[];
  path?: string;
  type?: 'group';
}

/**
 * CDM-PDD v12.0 兩層導航結構
 */
export const pddNavigation: PDDNavItem[] = [
  // 1. 基本資訊 (Basic Information)
  {
    key: 'basic-info',
    label: '基本資訊',
    labelEn: 'Basic Information',
    icon: InfoCircleOutlined,
    required: true,
    children: [
      {
        key: 'basic-title',
        label: '專案標題',
        labelEn: 'Project Title',
        path: '/projects/:projectId/pdd/basic/title',
        required: true,
      },
      {
        key: 'basic-scale',
        label: '專案規模',
        labelEn: 'Project Scale',
        path: '/projects/:projectId/pdd/basic/scale',
        required: true,
      },
      {
        key: 'basic-version',
        label: 'PDD 版本',
        labelEn: 'PDD Version',
        path: '/projects/:projectId/pdd/basic/version',
        required: true,
      },
      {
        key: 'basic-completion',
        label: '完成日期',
        labelEn: 'Completion Date',
        path: '/projects/:projectId/pdd/basic/completion',
        required: true,
      },
      {
        key: 'basic-participants',
        label: '專案參與者',
        labelEn: 'Project Participants',
        path: '/projects/:projectId/pdd/basic/participants',
        required: true,
      },
      {
        key: 'basic-host',
        label: '主辦國',
        labelEn: 'Host Party',
        path: '/projects/:projectId/pdd/basic/host',
        required: true,
      },
      {
        key: 'basic-methodologies',
        label: '應用方法學',
        labelEn: 'Applied Methodologies',
        path: '/projects/:projectId/pdd/basic/methodologies',
        required: true,
      },
      {
        key: 'basic-scopes',
        label: '部門範圍',
        labelEn: 'Sectoral Scopes',
        path: '/projects/:projectId/pdd/basic/scopes',
        required: true,
      },
      {
        key: 'basic-reductions',
        label: '減排量估算',
        labelEn: 'Estimated Reductions',
        path: '/projects/:projectId/pdd/basic/reductions',
        required: true,
      },
    ]
  },

  // 2. Section A: Description of Project Activity
  {
    key: 'section-a',
    label: 'A. 專案描述',
    labelEn: 'A. Project Description',
    icon: FileTextOutlined,
    required: true,
    children: [
      {
        key: 'a1-purpose',
        label: 'A.1 目的與概述',
        labelEn: 'A.1 Purpose and General Description',
        path: '/projects/:projectId/pdd/section-a/purpose',
        required: true,
      },
      {
        key: 'a2-location',
        label: 'A.2 專案位置',
        labelEn: 'A.2 Location',
        path: '/projects/:projectId/pdd/section-a/location',
        required: true,
      },
      {
        key: 'a3-technologies',
        label: 'A.3 技術與措施',
        labelEn: 'A.3 Technologies/Measures',
        path: '/projects/:projectId/pdd/section-a/technologies',
        required: true,
      },
      {
        key: 'a4-parties',
        label: 'A.4 參與方與組織',
        labelEn: 'A.4 Parties and Participants',
        path: '/projects/:projectId/pdd/section-a/parties',
        required: true,
      },
      {
        key: 'a5-funding',
        label: 'A.5 公共資金',
        labelEn: 'A.5 Public Funding',
        path: '/projects/:projectId/pdd/section-a/funding',
        required: true,
      },
      {
        key: 'a6-history',
        label: 'A.6 專案歷史',
        labelEn: 'A.6 History of Project',
        path: '/projects/:projectId/pdd/section-a/history',
        badge: 'NEW',
        required: true,
      },
      {
        key: 'a7-debundling',
        label: 'A.7 拆分檢查',
        labelEn: 'A.7 Debundling',
        path: '/projects/:projectId/pdd/section-a/debundling',
        badge: 'NEW',
        required: false, // Large-scale 填 "Not applicable"
      },
    ]
  },

  // 3. Section B: Application of Methodologies
  {
    key: 'section-b',
    label: 'B. 方法學應用',
    labelEn: 'B. Methodologies',
    icon: ExperimentOutlined,
    required: true,
    children: [
      // B.1-B.3 方法學基礎
      {
        key: 'b-methodology-group',
        label: '方法學基礎',
        labelEn: 'Methodology Foundation',
        type: 'group',
        children: [
          {
            key: 'b1-references',
            label: 'B.1 方法學參考',
            labelEn: 'B.1 References to Methodologies',
            path: '/projects/:projectId/pdd/section-b/references',
            required: true,
          },
          {
            key: 'b2-applicability',
            label: 'B.2 適用性證明',
            labelEn: 'B.2 Applicability',
            path: '/projects/:projectId/pdd/section-b/applicability',
            required: true,
          },
          {
            key: 'b3-boundary',
            label: 'B.3 專案邊界與 GHG',
            labelEn: 'B.3 Project Boundary and GHGs',
            path: '/projects/:projectId/pdd/section-b/boundary',
            required: true,
          },
        ]
      },
      // B.4-B.5 基準線與額外性
      {
        key: 'b-baseline-group',
        label: '基準線與額外性',
        labelEn: 'Baseline and Additionality',
        type: 'group',
        children: [
          {
            key: 'b4-baseline',
            label: 'B.4 基準線情境',
            labelEn: 'B.4 Baseline Scenario',
            path: '/projects/:projectId/pdd/section-b/baseline',
            required: true,
          },
          {
            key: 'b5-additionality',
            label: 'B.5 額外性證明',
            labelEn: 'B.5 Demonstration of Additionality',
            path: '/projects/:projectId/pdd/section-b/additionality',
            badge: 'NEW',
            required: true,
          },
        ]
      },
      // B.6 減排量估算
      {
        key: 'b-emissions-group',
        label: '減排量估算',
        labelEn: 'Emission Reductions',
        type: 'group',
        children: [
          {
            key: 'b61-choices',
            label: 'B.6.1 方法學選擇',
            labelEn: 'B.6.1 Methodological Choices',
            path: '/projects/:projectId/pdd/section-b/emissions/choices',
            required: true,
          },
          {
            key: 'b62-exante-data',
            label: 'B.6.2 事前固定參數',
            labelEn: 'B.6.2 Ex Ante Data',
            path: '/projects/:projectId/pdd/section-b/emissions/exante-data',
            required: true,
          },
          {
            key: 'b63-calculation',
            label: 'B.6.3 事前減排計算',
            labelEn: 'B.6.3 Ex Ante Calculation',
            path: '/projects/:projectId/pdd/section-b/emissions/calculation',
            required: true,
          },
          {
            key: 'b64-summary',
            label: 'B.6.4 減排量摘要',
            labelEn: 'B.6.4 Summary',
            path: '/projects/:projectId/pdd/section-b/emissions/summary',
            required: true,
          },
        ]
      },
      // B.7 監測計畫 (從 Section D 移至此)
      {
        key: 'b-monitoring-group',
        label: '監測計畫',
        labelEn: 'Monitoring Plan',
        type: 'group',
        badge: 'MOVED',
        children: [
          {
            key: 'b71-parameters',
            label: 'B.7.1 監測參數',
            labelEn: 'B.7.1 Data and Parameters',
            path: '/projects/:projectId/pdd/section-b/monitoring/parameters',
            required: true,
          },
          {
            key: 'b72-sampling',
            label: 'B.7.2 抽樣計畫',
            labelEn: 'B.7.2 Sampling Plan',
            path: '/projects/:projectId/pdd/section-b/monitoring/sampling',
            required: false,
          },
          {
            key: 'b73-other',
            label: 'B.7.3 其他監測要素',
            labelEn: 'B.7.3 Other Elements',
            path: '/projects/:projectId/pdd/section-b/monitoring/other',
            required: true,
          },
        ]
      },
    ]
  },

  // 4. Section C: Start Date, Crediting Period
  {
    key: 'section-c',
    label: 'C. 期間設定',
    labelEn: 'C. Crediting Period',
    icon: ClockCircleOutlined,
    required: true,
    children: [
      {
        key: 'c1-start-date',
        label: 'C.1 專案開始日期',
        labelEn: 'C.1 Start Date',
        path: '/projects/:projectId/pdd/section-c/start-date',
        required: true,
      },
      {
        key: 'c2-lifetime',
        label: 'C.2 預期營運壽命',
        labelEn: 'C.2 Operational Lifetime',
        path: '/projects/:projectId/pdd/section-c/lifetime',
        required: true,
      },
      {
        key: 'c3-crediting',
        label: 'C.3 計入期間',
        labelEn: 'C.3 Crediting Period',
        path: '/projects/:projectId/pdd/section-c/crediting',
        required: true,
      },
    ]
  },

  // 5. Section D: Environmental Impacts
  {
    key: 'section-d',
    label: 'D. 環境影響',
    labelEn: 'D. Environmental Impacts',
    icon: GlobalOutlined,
    required: true,
    children: [
      {
        key: 'd1-analysis',
        label: 'D.1 環境影響分析',
        labelEn: 'D.1 Analysis',
        path: '/projects/:projectId/pdd/section-d/analysis',
        required: true,
      },
      {
        key: 'd2-assessment',
        label: 'D.2 環境影響評估',
        labelEn: 'D.2 Assessment',
        path: '/projects/:projectId/pdd/section-d/assessment',
        required: false, // 如主辦國未要求可標註 "Not applicable"
      },
    ]
  },

  // 6. Section E: Local Stakeholder Consultation (從 G 提升)
  {
    key: 'section-e',
    label: 'E. 利害關係人諮詢',
    labelEn: 'E. Stakeholder Consultation',
    icon: TeamOutlined,
    badge: 'UPGRADED',
    required: true,
    children: [
      {
        key: 'e1-modalities',
        label: 'E.1 諮詢方式',
        labelEn: 'E.1 Modalities',
        path: '/projects/:projectId/pdd/section-e/modalities',
        required: true,
      },
      {
        key: 'e2-comments',
        label: 'E.2 意見摘要',
        labelEn: 'E.2 Summary of Comments',
        path: '/projects/:projectId/pdd/section-e/comments',
        required: true,
      },
      {
        key: 'e3-consideration',
        label: 'E.3 意見處理',
        labelEn: 'E.3 Consideration',
        path: '/projects/:projectId/pdd/section-e/consideration',
        required: true,
      },
    ]
  },

  // 7. Section F: Approval and Authorization (新增)
  {
    key: 'section-f',
    label: 'F. 核准與授權',
    labelEn: 'F. Approval and Authorization',
    icon: CheckCircleOutlined,
    badge: 'NEW',
    required: true,
    children: [
      {
        key: 'f1-approval',
        label: 'F.1 核准信函',
        labelEn: 'F.1 Letters of Approval',
        path: '/projects/:projectId/pdd/section-f/approval',
        required: true,
      },
      {
        key: 'f2-authorization',
        label: 'F.2 授權文件',
        labelEn: 'F.2 Authorization',
        path: '/projects/:projectId/pdd/section-f/authorization',
        required: true,
      },
    ]
  },

  // 8. Appendices
  {
    key: 'appendices',
    label: '附錄資料',
    labelEn: 'Appendices',
    icon: PaperClipOutlined,
    required: true,
    children: [
      {
        key: 'app1-contact',
        label: 'Appendix 1: 聯絡資訊',
        labelEn: 'Appendix 1: Contact Information',
        path: '/projects/:projectId/pdd/appendices/contact',
        required: true,
      },
      {
        key: 'app2-funding',
        label: 'Appendix 2: 公共資金聲明',
        labelEn: 'Appendix 2: Public Funding',
        path: '/projects/:projectId/pdd/appendices/funding',
        required: false,
      },
      {
        key: 'app3-applicability',
        label: 'Appendix 3: 方法學適用性',
        labelEn: 'Appendix 3: Applicability',
        path: '/projects/:projectId/pdd/appendices/applicability',
        required: false,
      },
      {
        key: 'app4-emissions',
        label: 'Appendix 4: 減排計算背景',
        labelEn: 'Appendix 4: Emission Calculations',
        path: '/projects/:projectId/pdd/appendices/emissions',
        required: false,
      },
      {
        key: 'app5-monitoring',
        label: 'Appendix 5: 監測計畫背景',
        labelEn: 'Appendix 5: Monitoring Plan',
        path: '/projects/:projectId/pdd/appendices/monitoring',
        required: false,
      },
      {
        key: 'app6-stakeholders',
        label: 'Appendix 6: 利害關係人意見',
        labelEn: 'Appendix 6: Stakeholder Comments',
        path: '/projects/:projectId/pdd/appendices/stakeholders',
        required: true,
      },
      {
        key: 'app7-changes',
        label: 'Appendix 7: 註冊後變更',
        labelEn: 'Appendix 7: Post-registration Changes',
        path: '/projects/:projectId/pdd/appendices/changes',
        badge: 'NEW',
        required: false, // 僅在有變更時填寫
      },
    ]
  },
];

/**
 * 取得扁平化的導航項目（用於麵包屑等）
 */
export const getFlattenedNavItems = (): Map<string, PDDNavItem> => {
  const map = new Map<string, PDDNavItem>();

  const flatten = (items: PDDNavItem[], parent?: PDDNavItem) => {
    items.forEach(item => {
      map.set(item.key, item);
      if (item.children) {
        flatten(item.children, item);
      }
    });
  };

  flatten(pddNavigation);
  return map;
};

/**
 * 根據 key 取得導航項目
 */
export const getNavItemByKey = (key: string): PDDNavItem | undefined => {
  const map = getFlattenedNavItems();
  return map.get(key);
};

/**
 * 取得必填項目列表
 */
export const getRequiredItems = (): PDDNavItem[] => {
  const required: PDDNavItem[] = [];

  const collect = (items: PDDNavItem[]) => {
    items.forEach(item => {
      if (item.required && item.path) {
        required.push(item);
      }
      if (item.children) {
        collect(item.children);
      }
    });
  };

  collect(pddNavigation);
  return required;
};

export default pddNavigation;
